import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Import the filter primitives DIRECTLY (not via a barrel) to keep the
// import graph explicit + tree-shake the heavier `<DateRangeFilter>`.
import { DateRangeFilter } from '../../../components/filters/DateRangeFilter';
import { FilterBar } from '../../../components/filters/FilterBar';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { REASON_OPTIONS, type RefundFilters } from '../_lib/refundFilters';

/**
 * Filter toolbar for the refunds history list (AUT-129) — reason
 * multi-select + order-id free-text + date range. Extracted from the page
 * so the page stays under the line cap. Every value is URL-state-driven
 * via the parent's `setFilters` patch.
 *
 * The reason multi-select offers the FULL six `ADMIN_REFUND_REASONS`
 * (architect guardrail #2 — history can contain any). NO search input
 * (the backend query schema carries no `q` free-text). Order is a UUID
 * free-text field (the backend's exact-match `orderId` filter; a typeahead
 * is a follow-up) — mirrors the payouts filter bar's inspector field.
 */
export interface RefundsFilterBarProps {
  filters: RefundFilters;
  setFilters: (
    patch: Partial<Record<keyof RefundFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const RefundsFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: RefundsFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <StatusMultiSelect
        status={filters.reason ?? []}
        options={REASON_OPTIONS}
        label={t('refunds.filters.reasonLabel')}
        onChange={(next) => setFilters({ reason: next })}
      />
      <TextField
        size="small"
        label={t('refunds.filters.orderLabel')}
        placeholder={t('refunds.filters.orderPlaceholder')}
        value={filters.orderId ?? ''}
        onChange={(e) => setFilters({ orderId: e.target.value || null })}
        sx={{ minWidth: 200 }}
      />
      <DateRangeFilter
        from={filters.dateFrom ?? null}
        to={filters.dateTo ?? null}
        onChange={(range) => setFilters({ dateFrom: range.from, dateTo: range.to })}
      />
    </FilterBar>
  );
};
